import get from 'lodash/get';
import React, {Component} from 'react';
import {FastField, ErrorMessage} from 'formik';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import messages from '../../../../res/translations/messages';
import {generateNextChoiceId} from '../../../services/idHelpers';
import MinusButton from './MinusButton';
import PlusButton from './PlusButton';
import UpButton from './UpButton';
import DownButton from './DownButton';
import Error from '../../Error';
import TooltipIcon from '../../TooltipIcon';
import RichEditorField from '../../rte/RichEditorField';
import DraggableCell from './DraggableCell';
import DraggableRow from './DraggableRow';
import DragDropTableBody from './DragDropTableBody';
import {previousQuestionsSupportingExpressions} from '../../../services/conditionBuilders';

class ShuffledRowLimitChoiceType extends Component {
    constructor(props) {
        super(props);
        this.state = {shuffled: false};

        this.addChoice = this.addChoice.bind(this);
        this.toggleShuffle = this.toggleShuffle.bind(this);
    }

    componentDidMount() {
        const {form, name} = this.props;
        const fields = get(form.values, name);
        const count = fields.filter((field) => field.shuffle).length;
        if (count > 0 && count === fields.length) {
            this.setState({shuffled: true});
        } else if (count === 0) {
            this.setState({shuffled: false});
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {form, name} = this.props;
        const fields = get(form.values, name);
        const count = fields.filter((field) => field.shuffle).length;
        if (count > 0 && count === fields.length && prevState.shuffled !== true) {
            this.setState({shuffled: true});
        } else if (count === 0 && prevState.shuffled !== false) {
            this.setState({shuffled: false});
        }
    }

    addChoice() {
        const choices = get(this.props.form.values, this.props.name);
        this.props.push({id: generateNextChoiceId(choices), value: '', shuffle: false, min: '', max: ''});
    }

    toggleShuffle(e) {
        const {replace, form, name} = this.props;
        const value = e.target.checked;
        const fields = get(form.values, name);
        this.setState({shuffled: value});

        if (value) {
            fields.forEach((field, index) => {
                replace(index, Object.assign({}, field, {shuffle: true}));
            });
        } else {
            fields.forEach((field, index) => {
                replace(index, Object.assign({}, field, {shuffle: false}));
            });
        }
    }

    render() {
        const {remove, move, form, name, previousPages} = this.props;
        const questions = previousQuestionsSupportingExpressions(previousPages);

        return (
            <table className="table table-sm table-borderless">
                <colgroup>
                    <col style={{width: 120}} />
                    <col />
                    <col style={{width: 110}} />
                    <col style={{width: 10}} />
                    <col style={{width: 80}} />
                    <col style={{width: 10}} />
                    <col style={{width: 80}} />
                    <col style={{width: 50}} />
                    <col style={{width: 50}} />
                </colgroup>
                <thead>
                    <tr>
                        <th className="pl-0"><FormattedMessage {...messages.surveys.form.label.choiceId} /></th>
                        <th><FormattedMessage {...messages.surveys.form.label.choiceLabel} /></th>
                        <th>
                            <div className="custom-control custom-checkbox">
                                <input className="custom-control-input" type="checkbox"
                                    checked={this.state.shuffled} onChange={this.toggleShuffle} id={`${name}.shuffled`} />
                                <label className="custom-control-label" htmlFor={`${name}.shuffled`}>
                                    <FormattedMessage {...messages.surveys.form.label.shuffled} />
                                    {' '}
                                    <TooltipIcon message={messages.surveys.form.tooltip.shuffled} />
                                </label>
                            </div>
                        </th>
                        <th colSpan="2">
                            <div className="custom-control-min-max">
                                <label>
                                    {<FormattedMessage {...messages.surveys.form.label.min} />}
                                </label>
                            </div>
                        </th>
                        <th colSpan="3">
                            <div className="custom-control-min-max">
                                <label>
                                    {<FormattedMessage {...messages.surveys.form.label.max} />}
                                </label>
                            </div>
                        </th>
                    </tr>
                </thead>
                <DragDropTableBody move={move}>
                    {get(form.values, name) && get(form.values, name).map((item, index, arr) => (
                        <DraggableRow item={item} index={index} key={index}>
                            <DraggableCell className="pl-0">
                                <FastField id={`${name}.${index}.id`} name={`${name}.${index}.id`} type="text"
                                    className={classNames('form-control',
                                        {error: get(form.errors, `${name}.${index}.id`) && get(form.touched, `${name}.${index}.id`)})} />
                                <ErrorMessage name={`${name}.${index}.id`}>
                                    {(message) => <Error reason={message.key} reasonParams={message.params} />}
                                </ErrorMessage>
                            </DraggableCell>
                            <DraggableCell>
                                <FastField id={`${name}.${index}.value`} name={`${name}.${index}.value`} component={RichEditorField}
                                    questions={questions}
                                    className={classNames('form-control',
                                        {error: get(form.errors, `${name}.${index}.value`) &&
                                            get(form.touched, `${name}.${index}.value`)})} />
                                <ErrorMessage name={`${name}.${index}.value`}>
                                    {(message) => <Error reason={message.key} reasonParams={message.params} />}
                                </ErrorMessage>
                            </DraggableCell>
                            <DraggableCell colSpan="2">
                                <FastField name={`${name}.${index}.shuffle`}>
                                    {({field}) => (
                                        <div className="custom-control custom-checkbox">
                                            <input type="checkbox" name={field.name} id={`${name}.${index}.shuffle`}
                                                className="custom-control-input"
                                                checked={field.value} onChange={field.onChange} />
                                            <label className="custom-control-label" htmlFor={`${name}.${index}.shuffle`}>&nbsp;</label>
                                        </div>
                                    )}
                                </FastField>
                            </DraggableCell>
                            <DraggableCell>
                                <FastField id={`${name}.${index}.min`} name={`${name}.${index}.min`} type="number"
                                    className={classNames('form-control',
                                        {error: get(form.errors, `${name}.${index}.min`) && get(form.touched, `${name}.${index}.min`)})} />
                                <ErrorMessage name={`${name}.${index}.min`}>
                                    {(message) => <Error reason={message.key} reasonParams={message.params} />}
                                </ErrorMessage>
                            </DraggableCell>
                            <DraggableCell />
                            <DraggableCell>
                                <FastField id={`${name}.${index}.max`} name={`${name}.${index}.max`} type="number"
                                    className={classNames('form-control',
                                        {error: get(form.errors, `${name}.${index}.max`) && get(form.touched, `${name}.${index}.max`)})} />
                                <ErrorMessage name={`${name}.${index}.max`}>
                                    {(message) => <Error reason={message.key} reasonParams={message.params} />}
                                </ErrorMessage>
                            </DraggableCell>
                            <DraggableCell className="border-right">
                                <div className="d-flex justify-content-center">
                                    <div className="flex-column">
                                        <div className="">{index > 0 && <UpButton move={move} index={index} />}</div>
                                        <div className="">{index + 1 < arr.length && <DownButton move={move} index={index} />}</div>
                                    </div>
                                </div>
                            </DraggableCell>
                            <DraggableCell>
                                <div className="d-flex justify-content-center">
                                    <div className="flex-column">
                                        <MinusButton remove={remove} index={index} />
                                    </div>
                                </div>
                            </DraggableCell>
                        </DraggableRow>
                    ))}
                </DragDropTableBody>
                <tfoot>
                    <tr>
                        <td colSpan="4" className="pl-0">
                            <PlusButton onClick={this.addChoice}>
                                <FormattedMessage {...messages.surveys.button.add_choice} />
                            </PlusButton>
                        </td>
                    </tr>
                </tfoot>
            </table>
        );
    }
}

export default ShuffledRowLimitChoiceType;
